import { Component, Input, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, ElementRef, AfterViewInit, Inject, PLATFORM_ID } from '@angular/core';
import { Asset, SearchProductsQuery, SearchVariantsQuery } from '../../../common/generated-types';
import { Subscription } from 'rxjs';
import { NGXLogger } from 'ngx-logger';
import { CartService } from '../../../core/providers/cart/cart.service';
import { StateService } from '../../../core/providers/state/state.service';
import { AddToCartResult, CollectionItemType, RoundedCornerSize } from '../../../common/enums';
import { ActivatedRoute, Router } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { getRoundedClass } from '../../../common/utils/styles-helper';

@Component({
  selector: 'vsf-product-card-horizontal-type',
  templateUrl: './product-card-horizontal-type.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductCardHorizontalTypeComponent implements OnInit, AfterViewInit {
  @Input() item!: SearchProductsQuery['search']['items'][number] | SearchVariantsQuery['search']['items'][number];
  @Input() displayType: CollectionItemType = CollectionItemType.Product;
  @Input() displaySubtitle = false;
  @Input() displayPrice = false;
  @Input() displayBuyButton = false;
  @Input() displayAlternativeImage = false;
  @Input() isMobile = false;
  @Input() productCardRoundedCorners: RoundedCornerSize = RoundedCornerSize.None;
  @Input() buttonText = 'Add to cart';
  @Input() buttonColorMode: 'default' | 'custom' = 'default';
  @Input() buttonBgColor = '';
  @Input() buttonTextColor = '';

  msrp: number | null = null;
  discountPercentage: number | null = null;
  inFlight = false;
  inStock = true;
  productBaseUrl = '';
  itemName = '';
  itemPrice: number | null = null;
  featuredAsset: Asset | null = null;
  cardWidth = 0;
  cardHeight = 0;
  isCustomizable = false;
  variantId: string | undefined;
  get currentUrl() { return this.router.url; }
  private subscription = new Subscription();
  backgroundColor: string | null = null;

  get linkCommands(): any[] {
    const base = (this.productBaseUrl || '/dp').split('/').filter(Boolean);
    return ['/', ...base, this.item?.slug].filter(Boolean);
  }
  get roundedClass(): string {
    return getRoundedClass(this.productCardRoundedCorners);
  }
  get isDisabled(): boolean {
    return this.inFlight || !this.inStock;
  }
  get resolvedButtonText(): string {
    const t = (this.buttonText ?? '').trim();
    return t.length ? t : 'ADD TO CART';
  }
  get defaultButtonClasses(): any {
    if (this.isDisabled) {
      return ['bg-zinc-600', 'text-white', 'border', 'border-[#2E3742]', 'cursor-not-allowed', 'opacity-90'];
    }
    if (this.buttonColorMode === 'custom') {
      return ['border', 'hover:opacity-90'];
    }
    return ['bg-white', 'text-[#2E3742]', 'border', 'border-[#2E3742]', 'hover:bg-primary-600', 'hover:text-white', 'hover:border-none', 'hover:font-bold'];
  }
  get buttonInlineStyle(): Record<string, string> | null {
    if (this.isDisabled || this.buttonColorMode !== 'custom') return null;
    const style: Record<string, string> = {};
    if (this.buttonBgColor) style['background-color'] = this.buttonBgColor;
    if (this.buttonTextColor) style['color'] = this.buttonTextColor;
    return style;
  }

  constructor(
    private cartService: CartService,
    private state: StateService,
    private changeDetector: ChangeDetectorRef,
    private logger: NGXLogger,
    private route: ActivatedRoute,
    private router: Router,
    private elementRef: ElementRef,
    @Inject(PLATFORM_ID) private platformId: object,
  ) {}

  ngOnInit() {
    if (this.item) {
      this.inStock = this.item.inStock;
      this.backgroundColor = (this.item as any).productCardBackgroundColor || null;
      if (this.displayType === CollectionItemType.Product) {
        const product = this.item as SearchProductsQuery['search']['items'][number];
        this.itemName = product.productName;
        this.featuredAsset = (product.productAsset as Asset) ?? null;
        this.isCustomizable = product?.isProductCustomizable || product?.isProductVariantCustomizable || false;
      } else {
        const variant = this.item as SearchVariantsQuery['search']['items'][number];
        this.itemName = variant.productVariantName;
        this.featuredAsset = (variant.productVariantAsset as Asset) || (variant.productAsset as Asset) || null;
        this.isCustomizable = variant?.isProductVariantCustomizable || variant?.isProductCustomizable || false;
        this.variantId = variant.productVariantId;
      }
      this.updatePricingInfo();
      this.changeDetector.markForCheck();
    }
    this.state.select(s => s.storePath).subscribe(storePath => {
        this.productBaseUrl = `${storePath}/dp`
    });
  }

  ngAfterViewInit() {
    setTimeout(() => this.calculateCardSize());
  }

  updatePricingInfo() {
    if (this.displayType === CollectionItemType.Product) {
      const p = this.item as SearchProductsQuery['search']['items'][number];
      if (p.price?.__typename === 'PriceRange' && p.price.min === p.price.max) this.itemPrice = p.price.min;
      else if (p.price?.__typename === 'SinglePrice') this.itemPrice = p.price.value ?? null;
      if (p.productVariantMSRP) this.msrp = p.productVariantMSRP;
    } else {
      const v = this.item as SearchVariantsQuery['search']['items'][number];
      if (v.price?.__typename === 'PriceRange' && v.price.min === v.price.max) this.itemPrice = v.price.min;
      else if (v.price?.__typename === 'SinglePrice') this.itemPrice = v.price.value ?? null;
      if (v.productVariantMSRP) this.msrp = v.productVariantMSRP;
    }
    if (this.itemPrice && this.msrp && this.msrp > this.itemPrice) {
      this.discountPercentage = ((this.msrp - this.itemPrice) / this.msrp) * 100;
    } else {
      this.discountPercentage = null;
    }
  }

  onButtonClick(event: MouseEvent) {
    if (this.isCustomizable) {
      this.router.navigate(
        [this.productBaseUrl, this.item.slug],
        { queryParams: { variantid: this.variantId } }
        );
    } else {
      event.preventDefault();
      event.stopPropagation();
      this.addToCartAndOpenDrawer();
    }
  }

  addToCartAndOpenDrawer() {
    if (!this.item) {
        return;
    }
    this.inFlight = true;
    this.changeDetector.markForCheck();

    this.subscription.add(
      this.cartService.addToCartAndOpenDrawer(this.item.productVariantId).subscribe({
        next: (result: AddToCartResult) => {
            this.inFlight = false;
            if (result === AddToCartResult.OUT_OF_STOCK) {
                this.inStock = false;
            }
            this.changeDetector.markForCheck();
        },
        error: err => {
          this.inFlight = false;
          this.logger.error('Error in addToCartAndOpenDrawer', err);
          this.changeDetector.markForCheck();
        },
      }),
    );
  }

  calculateCardSize() {
    if (!isPlatformBrowser(this.platformId)) return;
    const rect = (this.elementRef.nativeElement as HTMLElement).getBoundingClientRect();
    this.cardWidth = rect.width;
    this.cardHeight = rect.width;
    this.changeDetector.markForCheck();
  }
}
